'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { ChevronUp, Search } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useWidgetState } from '@/hooks/use-widget-state'

import { SearchWidget } from './search-widget'

interface StickySearchBarProps {
    initialFrom?: string
    initialFromId?: string
    initialTo?: string
    initialToId?: string
    initialMode?: string
    initialDate?: string
}

/** Format YYYY-MM-DD as "Thu, Feb 12" */
function formatShortDate(dateStr: string): string {
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day, 12, 0, 0)
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    })
}

/** Build search URL from widget state */
function buildSearchUrl(state: {
    from: string
    fromId: string
    to: string
    toId: string
    departDate: string
    tripType: string
    returnDate?: string
}): string | null {
    if (!state.from || !state.fromId || !state.to || !state.toId || !state.departDate) {
        return null
    }

    const from = encodeURIComponent(state.from)
    const to = encodeURIComponent(state.to)
    let url = `/depart/${from}/${to}/${state.departDate}/`

    if (state.tripType === 'round-trip' && state.returnDate) {
        url += `${state.returnDate}/`
    }

    const params = new URLSearchParams()
    if (state.fromId) {
        params.set('opid', state.fromId)
    }
    if (state.toId) {
        params.set('dpid', state.toId)
    }
    const qs = params.toString()
    if (qs) {
        url += `?${qs}`
    }

    return url
}

export function StickySearchBar({
    initialFrom,
    initialFromId,
    initialTo,
    initialToId,
    initialMode = 'bus',
    initialDate,
}: StickySearchBarProps) {
    const [visible, setVisible] = useState(false)
    const [expanded, setExpanded] = useState(false)
    const observerRef = useRef<IntersectionObserver | null>(null)
    const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const barRef = useRef<HTMLDivElement>(null)
    const mouseInBar = useRef(false)
    const mouseInPortal = useRef(false)
    const [mobileExpanded, setMobileExpanded] = useState(false)

    const widgetState = useWidgetState({
        from: initialFrom,
        fromId: initialFromId,
        to: initialTo,
        toId: initialToId,
        mode: initialMode as 'bus' | 'train' | 'ferry' | 'flight',
        departDate: initialDate ?? '',
    })

    const [state] = widgetState

    const cancelCollapse = useCallback(() => {
        if (collapseTimer.current) {
            clearTimeout(collapseTimer.current)
            collapseTimer.current = null
        }
    }, [])

    const scheduleCollapse = useCallback(() => {
        cancelCollapse()
        collapseTimer.current = setTimeout(() => {
            if (!mouseInBar.current && !mouseInPortal.current) {
                setExpanded(false)
            }
        }, 200)
    }, [cancelCollapse])

    const handleMouseEnter = useCallback(() => {
        mouseInBar.current = true
        cancelCollapse()
        setExpanded(true)
    }, [cancelCollapse])

    const handleMouseLeave = useCallback(() => {
        mouseInBar.current = false
        scheduleCollapse()
    }, [scheduleCollapse])

    // Track mouse over Radix portal content (calendars, autocomplete dropdowns)
    useEffect(() => {
        if (!visible) {
            return
        }

        const handlePointerOver = (e: PointerEvent) => {
            const target = e.target as Element
            if (target.closest('[data-radix-popper-content-wrapper]')) {
                mouseInPortal.current = true
                cancelCollapse()
            }
        }

        const handlePointerOut = (e: PointerEvent) => {
            const target = e.relatedTarget as Element | null
            if (!target?.closest('[data-radix-popper-content-wrapper]')) {
                mouseInPortal.current = false
                scheduleCollapse()
            }
        }

        document.addEventListener('pointerover', handlePointerOver)
        document.addEventListener('pointerout', handlePointerOut)

        return () => {
            document.removeEventListener('pointerover', handlePointerOver)
            document.removeEventListener('pointerout', handlePointerOut)
        }
    }, [visible, cancelCollapse, scheduleCollapse])

    useEffect(() => {
        const hero = document.querySelector('[data-section="hero"]')
        if (!hero) {
            return
        }

        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                setVisible(!entry.isIntersecting)
            },
            { threshold: 0 },
        )

        observerRef.current.observe(hero)

        return () => {
            observerRef.current?.disconnect()
        }
    }, [])

    // Clean up timer on unmount
    useEffect(() => {
        return () => cancelCollapse()
    }, [cancelCollapse])

    if (!visible) {
        return null
    }

    const navigateToSearch = () => {
        const url = buildSearchUrl(state)
        if (url) {
            window.location.href = url
        }
    }

    return (
        <div
            ref={barRef}
            data-testid="sticky-search-bar"
            id="sticky-search-bar"
            className="fixed top-[50px] right-0 left-0 z-20 overflow-hidden border-b border-[#d5dde6] bg-white shadow-[0_4px_8px_rgba(23,25,27,0.08)] md:top-0 md:z-40"
        >
            {/* Mobile layout */}
            <div className="md:hidden">
                {mobileExpanded ? (
                    <div className="px-4 py-3">
                        <SearchWidget widgetState={widgetState} formId="sticky-mobile" />
                        <button
                            type="button"
                            onClick={() => {
                                setMobileExpanded(false)
                            }}
                            aria-label="Collapse search"
                            className="mx-auto mt-2 flex items-center justify-center"
                        >
                            <ChevronUp className="h-6 w-6 text-[#8c939c]" />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 px-4 py-2">
                        <button
                            type="button"
                            onClick={() => {
                                setMobileExpanded(true)
                            }}
                            className="flex min-w-0 flex-1 flex-col rounded-[7px] border border-[#d9dee5] px-3 py-1.5 text-left"
                        >
                            <span className="truncate text-sm font-bold text-[#2e3033]">
                                {state.from && state.to ? (
                                    <>
                                        {state.from}
                                        <span className="mx-1 font-normal text-[#8c939c]">
                                            &rsaquo;
                                        </span>
                                        {state.to}
                                    </>
                                ) : (
                                    'Search trips'
                                )}
                            </span>
                            <span className="text-xs text-[#5f6368]">
                                {state.departDate
                                    ? formatShortDate(state.departDate)
                                    : 'Select date'}
                                {state.tripType === 'round-trip' && state.returnDate && (
                                    <> - {formatShortDate(state.returnDate)}</>
                                )}
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={navigateToSearch}
                            aria-label="Search"
                            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[7px] bg-[#d13b3b] text-white transition-colors hover:bg-[#b83232]"
                        >
                            <Search className="h-5 w-5" />
                        </button>
                    </div>
                )}
            </div>

            {/* Desktop hover-expand layout */}
            <div
                className={cn(
                    'hidden transition-[margin-top] duration-300 ease-in-out md:block',
                    expanded ? 'md:mt-0' : 'md:mt-[-36px]',
                )}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="mx-auto max-w-[980px] px-6 pt-2 pb-4">
                    <SearchWidget widgetState={widgetState} formId="sticky" />
                </div>
            </div>
        </div>
    )
}
